/**
 * useDebounce — Returns a value that updates only after `delay` ms of stability.
 * Useful for delaying API calls while user types.
 *
 * Example:
 *   const [search, setSearch] = useState('');
 *   const debouncedSearch = useDebounce(search, 400);
 *   useEffect(() => { fetchData(debouncedSearch); }, [debouncedSearch]);
 */
import { useEffect, useState } from 'react';

export function useDebounce(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

/**
 * useDebouncedCallback — Stable debounced function reference.
 * Use when you want to call a function (not derive a value).
 */
export function useDebouncedCallback(fn, delay = 400) {
  const [timeoutId, setTimeoutId] = useState(null);

  return (...args) => {
    if (timeoutId) clearTimeout(timeoutId);
    const id = setTimeout(() => fn(...args), delay);
    setTimeoutId(id);
  };
}

export default useDebounce;
